import { ActionTree, GetterTree, MutationTree } from 'vuex';
import { CartState } from '@/store/cart/types';
import {
    FlightProduct,
    ProductType,
    SeatAvailability,
    GordianWidgetStatus,
} from '@/api/Cart/cart.interfaces';
import { RootState } from '@/store/types';
import { Money } from '@libs/money/money.interfaces';
import { getAllSeatsForFlightProduct } from '@/helpers/seating';

export const seatingGetters: GetterTree<CartState, RootState> = {
    getGordianSeatingTotalPrice(state, getter): Money | null {
        const Currency = getter.getCurrency;
        const flightProducts = state.data.Products.filter(
            (product) => product.Type === 'Flight'
        ) as FlightProduct[];
        const seats = flightProducts.flatMap(getAllSeatsForFlightProduct);

        const total = seats.reduce(
            (accumulated, { Price }) => ({
                ...accumulated,
                Amount: accumulated.Amount + Price.Amount,
            }),
            { Amount: 0, Currency }
        );
        return total.Amount ? total : null;
    },
};
export const seatingMutations: MutationTree<CartState> = {
    setSeatingStatus({ data }, payload: { tripId: string; status: GordianWidgetStatus }) {
        data.Products = data.Products.map((product) => {
            if (product.Type === ProductType.Flight) {
                const flightProduct = product as FlightProduct;
                const gordian = flightProduct.Gordian;
                const isTripIdMatch = gordian?.TripId === payload.tripId;

                return isTripIdMatch
                    ? {
                          ...flightProduct,
                          Gordian: {
                              ...gordian,
                              WidgetStatus: payload.status,
                          },
                      }
                    : flightProduct;
            }
            return product;
        });
    },
    setSeatingAvailability(
        state,
        payload: { tripId: string; availability: SeatAvailability }
    ) {
        const seating = (
            state.data.Products.filter(
                ({ Type }) => Type === ProductType.Flight
            ) as FlightProduct[]
        ).find(({ Gordian }) => Gordian?.TripId === payload.tripId)?.Gordian;

        if (seating) {
            seating.WidgetStatus = GordianWidgetStatus.INITIAL;
            seating.SeatAvailability = payload.availability;
        }
    },
    setSeatingActive({ data }, payload: { tripId: string; active: boolean }) {
        data.Products = data.Products.map((product) => {
            if (product.Type === ProductType.Flight) {
                const flightProduct = product as FlightProduct;
                const gordian = flightProduct.Gordian;
                const isTripIdMatch = gordian?.TripId === payload.tripId;

                return isTripIdMatch
                    ? {
                          ...flightProduct,
                          Gordian: {
                              ...gordian,
                              Active: payload.active,
                          },
                      }
                    : flightProduct;
            }
            return product;
        });
    },
    updatePaxSeating({ data }, payload: FlightProduct) {
        data.Products = data.Products.map((product) => {
            if (product.Type === ProductType.Flight && product.Id === payload.Id) {
                const flightProduct = product as FlightProduct;
                return {
                    ...flightProduct,
                    Pax: flightProduct.Pax.map((pax, index) => ({
                        ...pax,
                        SelectedSeats: payload.Pax[index].SelectedSeats,
                    })),
                };
            }
            return product;
        });
    },
    clearPaxSeating({ data }, payload: FlightProduct) {
        data.Products = data.Products.map((product) => {
            if (product.Type === ProductType.Flight && product.Id === payload.Id) {
                const flightProduct = product as FlightProduct;
                return {
                    ...flightProduct,
                    Pax: flightProduct.Pax.map((pax) => ({
                        ...pax,
                        SelectedSeats: [],
                    })),
                };
            }
            return product;
        });
    },
};
export const seatingActions: ActionTree<CartState, RootState> = {
    clearSeatingError({ commit }, tripId: string) {
        commit('setSeatingStatus', {
            tripId,
            status: GordianWidgetStatus.INITIAL,
        });
    },
    setSeatingError({ commit }, tripId: string) {
        commit('setSeatingStatus', {
            tripId,
            status: GordianWidgetStatus.ERROR,
        });
    },
    setSeatingLoading({ commit }, payload: { tripId: string; loading: boolean }) {
        commit('setSeatingStatus', {
            tripId: payload.tripId,
            status: payload.loading
                ? GordianWidgetStatus.LOADING
                : GordianWidgetStatus.LOADED,
        });
    },
    setSeatingActive({ commit }, payload: { tripId: string; active: boolean }) {
        commit('setSeatingActive', payload);
    },
    setSeatingUpdating({ commit }, payload: { tripId: string; updating: boolean }) {
        commit('setSeatingStatus', {
            tripId: payload.tripId,
            status: payload.updating
                ? GordianWidgetStatus.UPDATING
                : GordianWidgetStatus.LOADED,
        });
    },
    setSeatingAvailability(
        { commit },
        payload: { tripId: string; availability: SeatAvailability }
    ) {
        commit('setSeatingAvailability', payload);
    },
    updatePaxSeating({ commit }, payload: FlightProduct) {
        commit('updatePaxSeating', payload);
    },
    clearPaxSeating({ commit }, payload: FlightProduct) {
        commit('clearPaxSeating', payload);
    },
};
